/**
 * The device properties the ad server matches a request against, from
 * `GET /v1/devices`. The endpoint groups them by dimension, each group listing
 * the values seen on the network.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** The dimensions the endpoint returns, named as the targeting fields are. */
export const DEVICE_DIMENSIONS = ['targetedDeviceTypes', 'targetedOSes', 'targetedBrands'] as const
export type DeviceDimension = (typeof DEVICE_DIMENSIONS)[number]

export type DeviceProperties = Record<DeviceDimension, string[]>

export const DevicesApi = {
  getAll: async (): Promise<DeviceProperties> => {
    const params = new URLSearchParams({ context: getContext() })
    const res = await apiFetch(`${API_BASE}/devices?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch device properties')
    const properties: DeviceProperties = {
      targetedDeviceTypes: [],
      targetedOSes: [],
      targetedBrands: [],
    }
    for (const group of toArray<Record<string, unknown>>(await res.json())) {
      const dimension = group.group as DeviceDimension
      if (!DEVICE_DIMENSIONS.includes(dimension)) continue
      properties[dimension] = ((group.properties as { value: string }[] | undefined) ?? [])
        .map((property) => property.value)
        .sort()
    }
    return properties
  },
}
